export default class ChartConfig {
  constructor() {
    // default config
    this.type = 'line';
    this.title = {};
    this.yAxisAnnotations = [];
    this.annotationOnYAxisText = '';
    this.labels = [];
    this.stacked = false;
    this.allowNegativeYAxis = true;
    this.labelFormatter = null;
    this.yAxisFormatter = null;
    this.forceNiceScale = null;
    this.xAxisFormatter = null;
    this.colors = null;
    this.seriesTypes = {};
    this.mixedYAxis = [];

    this.toolbar = {
      show: true,
      tools: {
        download: '<i class="fa fa-image"></i>',
        customIcons: [],
      },
    };

    this.xAxis = {
      categories: [],
    };

    this.defaultFontFamily = 'Source Sans Pro, Helvetica, Arial, sans-serif';
    this.defaultFontSize = '14px';
    this.displayAsTimeSeries = false;
  }

  isType(type) {
    this.type = type;
    return this;
  }

  isTimeSeries() {
    this.displayAsTimeSeries = true;
    return this;
  }

  isStacked() {
    this.stacked = true;
    return this;
  }

  withCustomTool(tool) {
    this.toolbar.tools.customIcons.push(tool);
    return this;
  }

  withAnnotationOnYAxis(text) {
    this.annotationOnYAxisText = text;

    return this;
  }

  withLabelFormatter(formatter) {
    this.labelFormatter = formatter;
    return this;
  }

  withToolTipFormatter(formatter) {
    this.toolTipFormatter = formatter;
    return this;
  }

  withYAxisFormatter(formatter) {
    this.yAxisFormatter = formatter;
    return this;
  }

  niceScale() {
    this.forceNiceScale = true;
    return this;
  }

  withXAxisFormatter(formatter) {
    this.xAxisFormatter = formatter;
    return this;
  }

  disableNegativeYAxis() {
    this.allowNegativeYAxis = false;
    return this;
  }

  setColors(colors) {
    this.colors = colors;
    return this;
  }

  setAnnotationOnYaxisValue(value, index = 0) {
    const annotation = {
      y: value,
      y2: null,
      strokeDashArray: [8, 4],
      borderColor: '#775DD0',
      label: {
        borderColor: '#775DD0',
        style: {
          color: '#fff',
          background: '#775DD0',
        },
        text: `${this.annotationOnYAxisText}: ${value}`,
      },
    };
    this.yAxisAnnotations[index] = annotation;
  }

  getYaxisAnnotation(index = 0) {
    return this.yAxisAnnotations[index];
  }

  setLabels(labels) {
    this.labels = labels;
    this.xAxis.categories = labels;
  }

  setYAxis(data) {
    this.mixedYAxis.push(data);
    return this;
  }

  withTitle(title) {
    this.title = {
      text: title,
      align: 'left',
      style: {
        fontSize: 22,
      },
    };
    return this;
  }

  setSeries(series) {
    this.series = series;
  }

  get() {
    // let's construct the config object
    const options = {};
    options.series = this.series;
    options.chart = {
      type: this.type,
      height: 350,
      stacked: this.stacked,
      width: '100%',
      fontFamily: this.defaultFontFamily,
      toolbar: this.toolbar,
      shadow: {
        enabled: true,
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 1,
      },
    };
    if (this.type === 'mixed') {
      options.chart.type = 'line';
    }
    options.dataLabels = {
      enabled: true,
      style: {
        fontSize: this.defaultFontSize,
      },
    };
    if (this.type === 'mixed') {
      options.dataLabels.enabled = false;
    }
    if (this.labelFormatter) {
      options.dataLabels.formatter = this.labelFormatter;
    }
    options.markers = {
      size: 4,
    };
    options.title = this.title;
    options.stroke = {
      curve: 'smooth',
    };
    if (this.type === 'mixed') {
      options.stroke.width = [1, 1, 4];
    }
    options.yaxis = {
      labels: {
        hideOverlappingLabels: false,
        style: {
          fontSize: this.defaultFontSize,
          fontFamily: this.defaultFontFamily,
        },
      },
    };
    options.xaxis = {
      type: 'category',
      labels: {
      },
    };
    if (this.type === 'mixed') {
      const yAxis = [];
      this.mixedYAxis.forEach((mixedYAxis) => {
        if (mixedYAxis.name === undefined) {
          mixedYAxis.name = mixedYAxis.title;
        }
        if (mixedYAxis.decimal === undefined) {
          mixedYAxis.decimal = 0;
        }
        if (mixedYAxis.show === undefined) {
          mixedYAxis.show = true;
        }
        const y = {
          show: mixedYAxis.show,
          seriesName: mixedYAxis.name,
          opposite: mixedYAxis.opposite,
          decimalsInFloat: mixedYAxis.decimal,
          axisTicks: {
            show: true,
          },
          axisBorder: {
            show: true,
            color: mixedYAxis.color,
          },
          labels: {
            style: {
              colors: mixedYAxis.color,
            },
          },
          title: {
            text: mixedYAxis.title,
            style: {
              color: mixedYAxis.color,
            },
          },
          tooltip: {
            enabled: true,
          },
        };
        yAxis.push(y);
      });
      options.yaxis = yAxis;
    }

    if (this.yAxisFormatter) {
      options.yaxis.labels.formatter = this.yAxisFormatter;
    }

    if (this.xAxisFormatter) {
      options.xaxis.labels.formatter = this.xAxisFormatter;
    }

    if (this.forceNiceScale) {
      options.yaxis.forceNiceScale = true;
    }

    if (!this.allowNegativeYAxis) {
      options.yaxis.min = 0;
    }
    options.grid = {
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5,
      },
    };
    options.plotOptions = {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    };
    options.tooltip = {
      enabled: false,
    };
    if (this.type === 'mixed') {
      options.tooltip = {
        fixed: {
          enabled: true,
          position: 'topLeft',
          offsetY: 30,
          offsetX: 60,
        },
        y: {
          formatter: this.toolTipFormatter,
        },
      };
    }
    options.noData = {
      text: 'There is nothing here',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      style: {
        color: undefined,
        fontSize: this.defaultFontSize,
        fontFamily: this.defaultFontFamily,
      },
    };
    options.labels = this.labels;

    // specific config if timeseries
    if (this.displayAsTimeSeries) {
      options.chart.zoom = {
        type: 'x',
        enabled: true,
        autoScaleYaxis: true,
      };
      options.chart.toolbar.autoSelected = 'zoom';
      options.xaxis = {
        type: 'datetime',
      };
    } else if (!this.xAxisFormatter) {
      options.xaxis = this.xAxis;
      if (this.type === 'mixed') {
        options.xaxis = {
          categories: this.xAxis,
        };
      }
    }

    const pluck = (arr, key) => arr.map((o) => o[key]);
    if (this.colors !== null) {
      const names = this.type === 'donut' ? this.labels : pluck(this.series, 'name');
      if (names) {
        options.colors = [];
        names.forEach((n) => {
          if (n in this.colors) {
            options.colors.push(this.colors[n]);
          }
        });
      }
    }

    return options;
  }
}
